import React, { useState } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import './CsvReviewUnified.css';

/**
 * Unified review screen for CSV HITL
 * Shows definitions with fields, highlights AI-suggested content
 */
const CsvReviewUnified = ({
  definitions,
  definitionSource,
  suggestionsFailed,
  invalidColumns = [],
  csvSummaries,
  onUpdateDefinition,
  onUpdateDefinitionField,
}) => {
  const [expandedSummaries, setExpandedSummaries] = useState({});

  const toggleSummary = (index) => {
    setExpandedSummaries(prev => ({ ...prev, [index]: !prev[index] }));
  };

  // Columns flagged by a 422 incomplete_review response, keyed by table + csv_column.
  const invalidColumnKeys = new Set(
    (invalidColumns || []).map(c => JSON.stringify([c.table, c.csv_column]))
  );

  const getBannerText = () => {
    if (definitionSource === 'llm_generated') {
      return 'All table definitions and field descriptions below have been generated by AI. Please review and edit as needed before approving.';
    }
    if (definitionSource === 'partial_document') {
      return 'Some definitions came from your uploaded document while others were generated by AI. AI-suggested items are highlighted below. Please review and edit as needed.';
    }
    return 'Definitions were loaded from your uploaded document. Please review and edit as needed before approving.';
  };

  return (
    <div className="csv-review-unified">
      {suggestionsFailed ? (
        <div className="csv-review-banner csv-review-banner-warning">
          <Icon name="info" className="csv-review-banner-icon" size={24} />
          <p>Automatic suggestions are unavailable right now — please enter column descriptions manually.</p>
        </div>
      ) : (
        <div className="csv-review-banner">
          <Icon name="auto_awesome" className="csv-review-banner-icon" size={24} />
          <p>{getBannerText()}</p>
        </div>
      )}

      {/* CSV Summaries (collapsible context) */}
      {csvSummaries && csvSummaries.length > 0 && (
        <div className="csv-review-summaries">
          <h4 className="csv-review-section-label">CSV File Context</h4>
          {csvSummaries.map((summary, idx) => (
            <div key={idx} className="csv-review-summary-card">
              <button
                className="csv-review-summary-toggle"
                onClick={() => toggleSummary(idx)}
              >
                <Icon name="description" className="csv-review-summary-icon" size={18} />
                <span className="csv-review-summary-filename">{summary.filename}</span>
                <span className="csv-review-summary-meta">{summary.row_count} rows</span>
                <Icon name={expandedSummaries[idx] ? 'expand_less' : 'expand_more'} />
              </button>
              {expandedSummaries[idx] && (
                <div className="csv-review-summary-content">
                  <div className="csv-review-summary-headers">
                    <span className="csv-review-label">Headers:</span>
                    <div className="csv-review-header-chips">
                      {summary.headers.map((h, i) => (
                        <span key={i} className="csv-review-header-chip">{h}</span>
                      ))}
                    </div>
                  </div>
                  {summary.sample_rows && summary.sample_rows.length > 0 && (
                    <div className="csv-review-sample-table-wrap">
                      <table className="csv-review-sample-table">
                        <thead>
                          <tr>
                            {summary.headers.map((h, i) => (
                              <th key={i}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {summary.sample_rows.slice(0, 3).map((row, ri) => (
                            <tr key={ri}>
                              {row.map((cell, ci) => (
                                <td key={ci}>{cell}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Definitions */}
      {definitions.map((def, defIndex) => (
        <div key={defIndex} className={`csv-review-def-card ${def.isAiSuggested ? 'csv-review-def-ai' : ''}`}>
          <div className="csv-review-def-header">
            <div className="csv-review-def-title-row">
              <h3 className="csv-review-def-name">{def.name}</h3>
              {def.badge === 'ai' && (
                <Chip className="csv-review-ai-badge">AI generated</Chip>
              )}
              {def.badge === 'document' && (
                <Chip variant="success" className="csv-review-doc-badge">From document</Chip>
              )}
            </div>
            <div className="csv-review-def-desc-field">
              <label className="csv-review-label">Table Description</label>
              <input
                type="text"
                className="resui-input"
                value={def.description}
                placeholder="Enter table description..."
                onChange={(e) => onUpdateDefinition(defIndex, 'description', e.target.value)}
              />
            </div>
          </div>

          <div className="csv-review-fields-section">
            <h4 className="csv-review-section-label">Fields</h4>
            <table className="csv-review-fields-table">
              <thead>
                <tr>
                  <th>Field Name</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                {def.fields.map((field, fieldIndex) => {
                  const isInvalid = invalidColumnKeys.has(JSON.stringify([def.snake_name, field.name]));
                  const rowClass = [
                    field.isAiSuggested ? 'csv-review-field-ai' : '',
                    isInvalid ? 'csv-review-field-invalid' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <tr key={fieldIndex} className={rowClass}>
                      <td>
                        {field.synthesized ? (
                          // No real CSV header — editable, prefilled with the AI-guessed name.
                          <input
                            type="text"
                            className="resui-input resui-input--compact csv-review-field-name-input"
                            value={field.name}
                            placeholder="Field name..."
                            onChange={(e) => onUpdateDefinitionField(defIndex, fieldIndex, 'name', e.target.value)}
                          />
                        ) : (
                          // Real header — locked.
                          <span className="csv-review-field-name">{field.name}</span>
                        )}
                      </td>
                      <td>
                        <div className="csv-review-field-desc-cell">
                          <input
                            type="text"
                            className={`resui-input resui-input--compact${isInvalid ? ' resui-input--error' : ''}`}
                            value={field.description}
                            placeholder="Enter description..."
                            onChange={(e) => onUpdateDefinitionField(defIndex, fieldIndex, 'description', e.target.value)}
                          />
                          {field.isAiSuggested && field.description && (
                            <Chip className="csv-review-ai-tag">AI suggested</Chip>
                          )}
                          {!field.description && (
                            <span className="csv-review-missing-tag">
                              <Icon name="warning" />
                              Description required
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CsvReviewUnified;
